/** Panel-chrome drag — repositions Firefly dock without toggling open state. */
import { useCallback, useEffect, useRef } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { ACTIVITY_DOCK_DRAG_THRESHOLD_PX } from "./activityDockPointerConstants";
import {
  releaseActivityDockPointerCapture,
  type ActivityDockPointerHandlers,
} from "./useActivityDockTapVsDrag";

type CaptureState = { target: HTMLElement; pointerId: number };
type DownState = {
  x: number;
  y: number;
  moved: boolean;
  grabOffsetX: number;
  grabOffsetY: number;
};

export function useActivityDockPanelRepositionDrag(args: {
  beginDrag: (clientX: number, clientY: number) => void;
  updateDrag: (clientX: number, clientY: number) => void;
  endDrag: (clientX: number, clientY: number, viewportW: number, viewportH: number) => void;
  cancelDrag: () => void;
}): ActivityDockPointerHandlers {
  const { beginDrag, updateDrag, endDrag, cancelDrag } = args;
  const downRef = useRef<DownState | null>(null);
  const captureRef = useRef<CaptureState | null>(null);

  const releaseCapture = useCallback(() => {
    releaseActivityDockPointerCapture(captureRef.current);
    captureRef.current = null;
  }, []);

  const abortInteraction = useCallback(() => {
    const wasDragging = downRef.current?.moved ?? false;
    releaseCapture();
    downRef.current = null;
    if (wasDragging) cancelDrag();
  }, [cancelDrag, releaseCapture]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") abortInteraction();
    };
    window.addEventListener("blur", abortInteraction);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("blur", abortInteraction);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [abortInteraction]);

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const target = e.currentTarget;
    target.setPointerCapture(e.pointerId);
    captureRef.current = { target, pointerId: e.pointerId };
    const rect = target.getBoundingClientRect();
    downRef.current = {
      x: e.clientX,
      y: e.clientY,
      moved: false,
      grabOffsetX: e.clientX - (rect.left + rect.width / 2),
      grabOffsetY: e.clientY - (rect.top + rect.height / 2),
    };
  }, []);

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      const down = downRef.current;
      if (!down) return;
      const dx = Math.abs(e.clientX - down.x);
      const dy = Math.abs(e.clientY - down.y);
      if (!down.moved) {
        if (dx < ACTIVITY_DOCK_DRAG_THRESHOLD_PX && dy < ACTIVITY_DOCK_DRAG_THRESHOLD_PX) return;
        down.moved = true;
        beginDrag(e.clientX - down.grabOffsetX, e.clientY - down.grabOffsetY);
      } else {
        updateDrag(e.clientX - down.grabOffsetX, e.clientY - down.grabOffsetY);
      }
    },
    [beginDrag, updateDrag],
  );

  const onPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      releaseCapture();
      const down = downRef.current;
      downRef.current = null;
      if (!down?.moved) return;
      endDrag(
        e.clientX - down.grabOffsetX,
        e.clientY - down.grabOffsetY,
        window.innerWidth,
        window.innerHeight,
      );
    },
    [endDrag, releaseCapture],
  );

  const onPointerCancel = useCallback(() => {
    releaseCapture();
    const down = downRef.current;
    downRef.current = null;
    if (down?.moved) cancelDrag();
  }, [cancelDrag, releaseCapture]);

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel };
}
